/**
 * Pre-submit validation registry.
 *
 * Some fields need an async check on "Next" that can't be expressed as a Yup
 * rule without firing on every keystroke (the form re-`trigger()`s on every
 * change). Such fields register a validator here; `useStepSubmit` runs them all
 * once, before evaluating API contracts or navigating. A validator returning
 * `false` blocks advancing — the field is responsible for showing its own inline
 * message (e.g. the promo-code field's orange "not valid" text).
 *
 * The provider must sit ABOVE `SchemaFormRenderer` so that both the renderer's
 * `useStepSubmit` (which runs validators) and the descendant field renderers
 * (which register them) share the same instance.
 */
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export type PreSubmitValidator = () => Promise<boolean>;

type PreSubmitValidationContextValue = {
  /** Register a validator; returns an unregister function for cleanup. */
  register: (validator: PreSubmitValidator) => () => void;
  /** Run every registered validator. Resolves false if any blocks. */
  runAll: () => Promise<boolean>;
};

const defaultValue: PreSubmitValidationContextValue = {
  register: () => () => undefined,
  runAll: async () => true,
};

const PreSubmitValidationContext =
  createContext<PreSubmitValidationContextValue>(defaultValue);

export const PreSubmitValidationProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const validatorsRef = useRef<Set<PreSubmitValidator>>(new Set());

  const register = useCallback((validator: PreSubmitValidator) => {
    validatorsRef.current.add(validator);
    return () => {
      validatorsRef.current.delete(validator);
    };
  }, []);

  const runAll = useCallback(async () => {
    const validators = Array.from(validatorsRef.current);
    // A validator that throws is treated as non-blocking (fail open) — a check
    // erroring out must not trap the user on the step.
    const results = await Promise.all(
      validators.map((validate) => validate().catch(() => true)),
    );
    return results.every(Boolean);
  }, []);

  const value = useMemo(() => ({ register, runAll }), [register, runAll]);

  return (
    <PreSubmitValidationContext.Provider value={value}>
      {children}
    </PreSubmitValidationContext.Provider>
  );
};

export const usePreSubmitValidation = () =>
  useContext(PreSubmitValidationContext);
